import { useState, useEffect, useMemo, useRef, ChangeEvent } from 'react'
import {
  Alert,
  Icon,
  InputMenu,
  LoadingModal,
  Modal,
  MultiselectMenu,
  SlimButton,
  Tooltip,
  Typography
} from 'components'
import ModalReport from '../ModalReport'
import { useSelector, useDispatch } from 'store/store'
import { Controller, useFormContext } from 'react-hook-form'
import { useCommercialStructure } from 'commons/customHooks'
import {
  getAnalysisQuotas,
  sendAnalysisUpload,
  setSuccessUpload
} from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { toast } from 'react-toastify'
import { Channels, criticismTypeOptions } from 'constants/enums'

import * as S from './styles'

export interface FilterParams {
  filtroMaterial?: string
  materialEstrategico?: boolean
  caixaGrade?: string
}

type FilterProps = {
  onSubmit: () => void
  onClear: () => void
  disableFilters: boolean
}

export const Filter = ({ onSubmit, onClear, disableFilters }: FilterProps) => {
  const dispatch = useDispatch()
  const { loadingReport, loadingUpload, successUpload, filterQuotas } = useSelector(
    (state) => state.extraVolume
  )
  const {
    offices: officesCommercialStructure,
    regionals: regionalsCommercialStructure,
    zones: zonesCommercialStructure
  } = useSelector((state) => state.shared)

  const {
    getMarkets,
    getOffices,
    getOfficesByMarkets,
    getRegionalsByOffices,
    getZonesByRegionals
  } = useCommercialStructure()

  const { control, setValue } = useFormContext()
  const [openReportModal, setOpenReportModal] = useState(false)

  const xlsxFileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleUploadClick = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click()
    }
  }
  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const uploadedFile = event.target.files[0]
      if (uploadedFile.type === xlsxFileType) {
        dispatch(sendAnalysisUpload(uploadedFile))
      } else {
        toast.error('Só é possível enviar planilhas no formato ".xlsx".')
      }
      event.target.value = ''
    }
  }

  const uploadSetOpen = (open: boolean) => {
    dispatch(setSuccessUpload(open))
  }

  const submitFilter = () => {
    onSubmit()
  }

  useEffect(() => {
    dispatch(getAnalysisQuotas([]))
  }, [])

  // NOTE: Commercial Structure is loaded in Home page, but when user access directly to Extra Volume page, it is not loaded
  useEffect(() => {
    // TODO: Check a better way to handle loading of initial commercial structure when force reload - W.I.P
    if (!officesCommercialStructure.length) {
      dispatch(getCommercialStructure())
    }
  }, [officesCommercialStructure])

  const filterValue = (value: string[]) => {
    if (value.length > 1) {
      return value.filter((v) => v !== '')
    } else if (!value.length) {
      return ['']
    } else {
      return value
    }
  }

  const { watch } = useFormContext()

  const selectedMarkets = watch('Mercado') as Channels[]
  const selectedOffices = watch('EscritorioVenda')
  const selectedRegions = watch('EquipeVenda')
  const selectedZones = watch('RegiaoVenda')

  useEffect(() => {
    dispatch(getAnalysisQuotas([]))
  }, [])

  const offices = useMemo(() => {
    if (selectedMarkets.length) {
      const validOffices = getOfficesByMarkets(selectedMarkets)
      const newSelected = validOffices
        .filter((office) => selectedOffices.includes(office.id))
        .map((office) => office.id)
      setValue('EscritorioVenda', newSelected)
      return validOffices
    }

    return getOffices()
  }, [selectedMarkets, officesCommercialStructure])

  const regionals = useMemo(() => {
    if (selectedOffices.length) {
      const validRegions = getRegionalsByOffices(selectedOffices)
      const newSelected = validRegions
        .filter((region) => selectedRegions.includes(region.id))
        .map((region) => region.id)
      setValue('EquipeVenda', newSelected)
      return validRegions
    }

    const regionalsOffices = offices.map((office) => office.id)
    return getRegionalsByOffices(regionalsOffices)
  }, [selectedOffices, selectedMarkets, regionalsCommercialStructure, offices])

  const zones = useMemo(() => {
    if (selectedRegions.length) {
      const validZones = getZonesByRegionals(selectedRegions)
      const newSelected = validZones
        .filter((zone) => selectedZones.includes(zone.id))
        .map((zone) => zone.id)
      setValue('RegiaoVenda', newSelected)
      return validZones
    }

    return getZonesByRegionals(regionals.map((regional) => regional.id))
  }, [selectedRegions, selectedOffices, selectedMarkets, zonesCommercialStructure])

  return (
    <S.Wrapper>
      <S.Header>
        <S.TitleContainer>
          <Typography design="hav-h5-text">Volume Extra</Typography>
          <Typography design="hav-default-04-text">
            Exibindo lista de solicitações de volume extra.
          </Typography>
        </S.TitleContainer>

        <S.ActionsContainer>
          <S.FileInput
            type="file"
            accept={xlsxFileType}
            onChange={handleFileSelect}
            ref={fileInputRef}
          />
          <SlimButton
            type="button"
            variant="secondary"
            content="Carregar arquivo"
            icon={<Icon icon="upload" width="15" />}
            loading={loadingUpload}
            onClick={handleUploadClick}
          />
          <SlimButton
            type="button"
            variant="secondary"
            content="Baixar"
            icon={<Icon icon="download" width="15" />}
            loading={loadingReport}
            onClick={() => setOpenReportModal(true)}
          />
        </S.ActionsContainer>
      </S.Header>

      <S.Form>
        <Controller
          name="TipoCritica"
          control={control}
          render={({ field }) => (
            <MultiselectMenu
              fullWidth
              label="Status"
              selectedOptions={field.value ?? []}
              onChange={(value) => field.onChange(filterValue(value))}
              optionsList={criticismTypeOptions}
              clearAction={() => setValue('TipoCritica', [])}
              checkAllAction={() => {
                setValue(
                  'TipoCritica',
                  criticismTypeOptions.map((item) => item.label)
                )
              }}
              disabled={disableFilters}
            />
          )}
        />
        <Controller
          name="Materiais"
          control={control}
          render={({ field }) => (
            <InputMenu
              fullWidth
              label="Material"
              placeholder="Busque por um ou mais códigos de material"
              value={field.value}
              onChange={(e) => {
                const value = e.target.value
                const batch = value.split('\n').filter((item) => item !== '')
                setValue('Materias', batch) // TODO: check if this is correct
                field.onChange(value)
              }}
              clearAction={() => {
                setValue('Materiais', '') // TODO: check if this is correct
                setValue('Materias', ['']) // TODO: check if this is correct
              }}
              disabled={disableFilters}
            />
          )}
        />
        <Controller
          name="Mercado"
          control={control}
          render={({ field }) => (
            <MultiselectMenu
              fullWidth
              label="Mercado"
              selectedOptions={field.value ?? []}
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={getMarkets().map((market) => ({
                label: market.description,
                value: market.id
              }))}
              clearAction={() => setValue('Mercado', [])}
              checkAllAction={() => {
                setValue(
                  'Mercado',
                  getMarkets().map((item) => item.id)
                )
              }}
              disabled={disableFilters}
            />
          )}
        />
        <Controller
          name="EscritorioVenda"
          control={control}
          render={({ field }) => (
            <MultiselectMenu
              fullWidth
              label="Escritório"
              selectedOptions={field.value ?? []}
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={offices?.map((office) => ({
                label: `${office.id} - ${office.description}`,
                value: office.id
              }))}
              clearAction={() => {
                setValue('EscritorioVenda', [])
              }}
              checkAllAction={() => {
                setValue(
                  'EscritorioVenda',
                  offices.map((office) => office.id)
                )
              }}
              disabled={disableFilters}
            />
          )}
        />
        <Controller
          name="EquipeVenda"
          control={control}
          render={({ field }) => (
            <MultiselectMenu
              fullWidth
              label="Regional"
              selectedOptions={field.value ?? []}
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={regionals.map((regional) => ({
                label: `${regional.id} - ${regional.description}`,
                value: regional.id
              }))}
              clearAction={() => {
                setValue('EquipeVenda', [])
              }}
              checkAllAction={() => {
                setValue(
                  'EquipeVenda',
                  regionals.map((regional) => regional.id)
                )
              }}
              disabled={disableFilters}
            />
          )}
        />
        <Controller
          name="RegiaoVenda"
          control={control}
          render={({ field }) => (
            <MultiselectMenu
              fullWidth
              label="Zona"
              hasSearch
              searchPlaceholder="Buscar"
              selectedOptions={field.value ?? []}
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={zones?.map((zone) => {
                return {
                  label: `${zone.id} - ${zone.description}`,
                  value: zone.id
                }
              })}
              clearAction={() => setValue('RegiaoVenda', [''])}
              checkAllAction={() => {
                setValue(
                  'RegiaoVenda',
                  zones.map((zone) => zone.id)
                )
              }}
              disabled={disableFilters}
            />
          )}
        />
        <Controller
          name="MesesCota"
          control={control}
          render={({ field }) => (
            <MultiselectMenu
              fullWidth
              label="Mês cota"
              selectedOptions={field.value}
              onChange={(value) => field.onChange(filterValue(value))}
              optionsList={filterQuotas.map((quota) => {
                return {
                  label: quota.ds_ano_mes_cota,
                  value: quota.dt_ano_mes_cota
                }
              })}
              clearAction={() => setValue('MesesCota', [''])}
              checkAllAction={() => {
                setValue(
                  'MesesCota',
                  filterQuotas.map((quota) => quota.dt_ano_mes_cota)
                )
              }}
              disabled={!filterQuotas.length || disableFilters}
            />
          )}
        />
        <SlimButton
          type="submit"
          variant="primary"
          content="Buscar"
          size="sm"
          onClick={submitFilter}
          disabled={disableFilters}
        />
        <Tooltip title="Limpar filtros" arrow>
          <span>
            <SlimButton
              id="filters-clear-button"
              variant="secondary"
              onClick={onClear}
              type="button"
              icon={<Icon icon="clear-all" size="20" />}
              disabled={disableFilters}
            />
          </span>
        </Tooltip>
      </S.Form>
      <ModalReport open={openReportModal} setOpen={setOpenReportModal} />
      {loadingUpload && <LoadingModal />}
      <Modal
        modalWidth={450}
        open={successUpload}
        setOpen={uploadSetOpen}
        showClose={false}
        title={
          <Alert
            kind="success"
            title="Análise dos pedidos iniciada!"
            message="Assim que o sistema concluir a análise, uma notificação será enviada para o seu email."
          />
        }
        buttonText="Fechar"
        buttonAction={() => uploadSetOpen(false)}
      >
        <S.ModalText>
          Enquanto aguarda por um retorno do Portal, você pode enviar novas análises.
        </S.ModalText>
      </Modal>
    </S.Wrapper>
  )
}
